import { Injectable } from '@angular/core';
import { Condition, InputType, QuestionCard, QuestionOption, QuestionPrefix, QuestionType } from '@empathy/survey/base';
import { Question } from '@empathy/survey-core';

import { QuestionFormInstanceByType } from '../models/type-forms/form-type-instance.constant';
import { QuestionForm } from '../models/type-forms/question-form.model';

@Injectable({ providedIn: 'root' })
export class DemographicQuestionsService {
  mapGenderQuestion(nextId: number, pageId: string): QuestionForm {
    const questionToAdd = new Question();
    questionToAdd.id = `${QuestionPrefix.Question}${nextId}`;
    questionToAdd.pageId = pageId;
    questionToAdd.type = QuestionType.SingleChoice;
    questionToAdd.title = 'What is your gender?';
    questionToAdd.options = [
      { id: 'opt_1', value: '1', text: 'Male' },
      { id: 'opt_2', value: '2', text: 'Female' },
      { id: 'opt_3', value: '3', text: 'Transgender' },
      { id: 'opt_4', value: '4', text: 'Gender Fluid' },
      { id: 'opt_5', value: '5', text: 'Prefer not to say' }
    ] as QuestionOption[];
    questionToAdd.useOtherInd = true;
    questionToAdd.otherLabel = 'Other (please specify)';

    const questionFormToAdd = QuestionFormInstanceByType(QuestionType.SingleChoice);
    questionFormToAdd.init(questionToAdd);

    return questionFormToAdd;
  }

  mapAgeQuestion(nextId: number, pageId: string): QuestionForm {
    const questionToAdd = new Question();
    questionToAdd.id = `${QuestionPrefix.Question}${nextId}`;
    questionToAdd.pageId = pageId;
    questionToAdd.type = QuestionType.Input;
    questionToAdd.inputType = InputType.Numbers;
    questionToAdd.title = 'What age are you?';
    questionToAdd.description = 'Please enter a whole number';

    const questionFormToAdd = QuestionFormInstanceByType(QuestionType.Input);
    questionFormToAdd.init(questionToAdd);

    return questionFormToAdd;
  }

  mapAgeTerminateQuestion(nextId: number, pageId: string): QuestionForm {
    const questionToAdd = new Question();
    questionToAdd.id = `${QuestionPrefix.Question}${nextId}`;
    questionToAdd.pageId = pageId;
    questionToAdd.type = QuestionType.Terminate;
    questionToAdd.title = 'Terminate if under 18';
    questionToAdd.visibleWhen = [
      { questionId: `${QuestionPrefix.Question}${nextId - 1}`, operator: 'LESS_THAN', valueToMatch: ['18'], cardId: null, group: null } as Condition
    ];

    const questionFormToAdd = QuestionFormInstanceByType(QuestionType.Terminate);
    questionFormToAdd.init(questionToAdd);

    return questionFormToAdd;
  }

  mapAgeHiddenQuestion(nextId: number, ageQuestionId: string): QuestionForm {
    const questionToAdd = new Question();
    questionToAdd.id = `${QuestionPrefix.HiddenQuestion}${nextId}`;
    questionToAdd.type = QuestionType.HiddenChoice;
    questionToAdd.title = 'Age Range (based on age)';
    questionToAdd.options = [
      {
        id: 'opt_1',
        value: '1',
        text: 'Younger than 18',
        selectedWhen: [{ questionId: ageQuestionId, operator: 'LESS_THAN', valueToMatch: ['18'] }]
      },
      {
        id: 'opt_2',
        value: '2',
        text: '18-24 years old',
        selectedWhen: [
          { questionId: ageQuestionId, operator: 'GREATER_THAN_OR_EQUAL', valueToMatch: ['18'] },
          'AND',
          { questionId: ageQuestionId, operator: 'LESS_THAN_OR_EQUAL', valueToMatch: ['24'] }
        ]
      },
      {
        id: 'opt_3',
        value: '3',
        text: '25-34 years old',
        selectedWhen: [
          { questionId: ageQuestionId, operator: 'GREATER_THAN_OR_EQUAL', valueToMatch: ['25'] },
          'AND',
          { questionId: ageQuestionId, operator: 'LESS_THAN_OR_EQUAL', valueToMatch: ['34'] }
        ]
      },
      {
        id: 'opt_4',
        value: '4',
        text: '35-44 years old',
        selectedWhen: [
          { questionId: ageQuestionId, operator: 'GREATER_THAN_OR_EQUAL', valueToMatch: ['35'] },
          'AND',
          { questionId: ageQuestionId, operator: 'LESS_THAN_OR_EQUAL', valueToMatch: ['44'] }
        ]
      },
      {
        id: 'opt_5',
        value: '5',
        text: '45-54 years old',
        selectedWhen: [
          { questionId: ageQuestionId, operator: 'GREATER_THAN_OR_EQUAL', valueToMatch: ['45'] },
          'AND',
          { questionId: ageQuestionId, operator: 'LESS_THAN_OR_EQUAL', valueToMatch: ['54'] }
        ]
      },
      {
        id: 'opt_6',
        value: '6',
        text: '55-64 years old',
        selectedWhen: [
          { questionId: ageQuestionId, operator: 'GREATER_THAN_OR_EQUAL', valueToMatch: ['55'] },
          'AND',
          { questionId: ageQuestionId, operator: 'LESS_THAN_OR_EQUAL', valueToMatch: ['64'] }
        ]
      },
      {
        id: 'opt_7',
        value: '7',
        text: '65+ years old',
        selectedWhen: [{ questionId: ageQuestionId, operator: 'GREATER_THAN_OR_EQUAL', valueToMatch: ['65'] }]
      }
    ] as QuestionOption[];

    const questionFormToAdd = QuestionFormInstanceByType(QuestionType.HiddenChoice);
    questionFormToAdd.init(questionToAdd);

    return questionFormToAdd;
  }

  mapRegionQuestion(nextId: number, pageId: string): QuestionForm {
    const questionToAdd = new Question();
    questionToAdd.id = `${QuestionPrefix.Question}${nextId}`;
    questionToAdd.pageId = pageId;
    questionToAdd.type = QuestionType.Dropdown;
    questionToAdd.title = 'In which of the following counties do you currently live?';
    questionToAdd.options = [
      { id: 'opt_1', value: '1', text: 'Carlow' },
      { id: 'opt_2', value: '2', text: 'Cavan' },
      { id: 'opt_3', value: '3', text: 'Clare' },
      { id: 'opt_4', value: '4', text: 'Cork' },
      { id: 'opt_5', value: '5', text: 'Donegal' },
      { id: 'opt_6', value: '6', text: 'Dublin' },
      { id: 'opt_7', value: '7', text: 'Galway' },
      { id: 'opt_8', value: '8', text: 'Kerry' },
      { id: 'opt_9', value: '9', text: 'Kildare' },
      { id: 'opt_10', value: '10', text: 'Kilkenny' },
      { id: 'opt_11', value: '11', text: 'Laois' },
      { id: 'opt_12', value: '12', text: 'Leitrim' },
      { id: 'opt_13', value: '13', text: 'Limerick' },
      { id: 'opt_14', value: '14', text: 'Longford' },
      { id: 'opt_15', value: '15', text: 'Louth' },
      { id: 'opt_16', value: '16', text: 'Mayo' },
      { id: 'opt_17', value: '17', text: 'Meath' },
      { id: 'opt_18', value: '18', text: 'Monaghan' },
      { id: 'opt_19', value: '19', text: 'Offaly' },
      { id: 'opt_20', value: '20', text: 'Roscommon' },
      { id: 'opt_21', value: '21', text: 'Sligo' },
      { id: 'opt_22', value: '22', text: 'Tipperary' },
      { id: 'opt_23', value: '23', text: 'Waterford' },
      { id: 'opt_24', value: '24', text: 'Westmeath' },
      { id: 'opt_25', value: '25', text: 'Wexford' },
      { id: 'opt_26', value: '26', text: 'Wicklow' },
      { id: 'opt_27', value: '27', text: 'Antrim' },
      { id: 'opt_28', value: '28', text: 'Armagh' },
      { id: 'opt_29', value: '29', text: 'Fermanagh' },
      { id: 'opt_30', value: '30', text: 'Derry/Londonderry' },
      { id: 'opt_31', value: '31', text: 'Down' },
      { id: 'opt_32', value: '32', text: 'Tyrone' }
    ] as QuestionOption[];

    const questionFormToAdd = QuestionFormInstanceByType(QuestionType.Dropdown);
    questionFormToAdd.init(questionToAdd);

    return questionFormToAdd;
  }

  mapRegionHiddenQuestion(nextId: number, regionQuestionId: string): QuestionForm {
    const questionToAdd = new Question();
    questionToAdd.id = `${QuestionPrefix.HiddenQuestion}${nextId}`;
    questionToAdd.type = QuestionType.HiddenChoice;
    questionToAdd.title = 'Region (based on county)';
    questionToAdd.options = [
      {
        id: 'opt_1',
        value: '1',
        text: 'Dublin',
        selectedWhen: [{ questionId: regionQuestionId, operator: 'EQUAL', valueToMatch: ['opt_6'] }]
      },
      {
        id: 'opt_2',
        value: '2',
        text: 'Rest of Leinster',
        selectedWhen: [
          {
            questionId: regionQuestionId,
            operator: 'EQUAL_ANY',
            valueToMatch: ['opt_1', 'opt_9', 'opt_10', 'opt_11', 'opt_14', 'opt_15', 'opt_17', 'opt_19', 'opt_24', 'opt_25', 'opt_26']
          }
        ]
      },
      {
        id: 'opt_4',
        value: '4',
        text: 'Munster',
        selectedWhen: [
          {
            questionId: regionQuestionId,
            operator: 'EQUAL_ANY',
            valueToMatch: ['opt_3', 'opt_4', 'opt_8', 'opt_13', 'opt_22', 'opt_23']
          }
        ]
      },
      {
        id: 'opt_5',
        value: '5',
        text: 'Connacht',
        selectedWhen: [
          {
            questionId: regionQuestionId,
            operator: 'EQUAL_ANY',
            valueToMatch: ['opt_7', 'opt_12', 'opt_16', 'opt_20', 'opt_21']
          }
        ]
      },
      {
        id: 'opt_6',
        value: '6',
        text: 'Ulster',
        selectedWhen: [
          {
            questionId: regionQuestionId,
            operator: 'EQUAL_ANY',
            valueToMatch: ['opt_2', 'opt_5', 'opt_18']
          }
        ]
      }
    ] as QuestionOption[];

    const questionFormToAdd = QuestionFormInstanceByType(QuestionType.HiddenChoice);
    questionFormToAdd.init(questionToAdd);

    return questionFormToAdd;
  }

  mapEmploymentStatusQuestion(nextId: number, pageId: string): QuestionForm {
    const questionToAdd = new Question();
    questionToAdd.id = `${QuestionPrefix.Question}${nextId}`;
    questionToAdd.pageId = pageId;
    questionToAdd.type = QuestionType.SingleChoice;
    questionToAdd.title = 'What is your current employment status?';
    questionToAdd.description =
      'If more than one option is applicable, e.g. you are employed part-time and a student, please select the activity that takes up the most of your time.';
    questionToAdd.options = [
      { id: 'opt_1', value: '1', text: 'Working full time (35 or more hours per week)' },
      { id: 'opt_2', value: '2', text: 'Working part-time (less than 35 hours per week)' },
      { id: 'opt_3', value: '3', text: 'Self-employed' },
      { id: 'opt_4', value: '4', text: 'Unemployed and looking for work' },
      { id: 'opt_5', value: '5', text: 'Looking after my home/family full time' },
      { id: 'opt_6', value: '6', text: 'Student' },
      { id: 'opt_7', value: '7', text: 'Retired' },
      { id: 'opt_8', value: '8', text: 'Unable to work' }
    ] as QuestionOption[];
    questionToAdd.useOtherInd = true;
    questionToAdd.otherLabel = 'Other (please specify)';

    const questionFormToAdd = QuestionFormInstanceByType(QuestionType.SingleChoice);
    questionFormToAdd.init(questionToAdd);

    return questionFormToAdd;
  }

  mapOccupationalGroupQuestion(nextId: number, pageId: string): QuestionForm {
    const questionToAdd = new Question();
    questionToAdd.id = `${QuestionPrefix.Question}${nextId}`;
    questionToAdd.pageId = pageId;
    questionToAdd.type = QuestionType.SingleChoice;
    questionToAdd.title = `Please indicate to which occupational group the <strong>chief income earner</strong> in your household belongs, or which group fits best.
The chief income earner is the person in your household with the largest income; this could be yourself or another person in your household.`;

    questionToAdd.options = [
      { id: 'opt_1', value: '1', text: 'Higher managerial/ professional/ administrative (e.g. Doctor, Solicitor, Board Director)' },
      {
        id: 'opt_2',
        value: '2',
        text: 'Intermediate managerial/ professional/ administrative (e.g. Board director small organisation, middle manager, principle officer in civil service)'
      },
      { id: 'opt_3', value: '3', text: 'Supervisory or clerical/ junior managerial/ professional/ administrative (e.g. Office worker, Salesperson)' },
      { id: 'opt_4', value: '4', text: 'Student' },
      { id: 'opt_5', value: '5', text: 'Skilled manual worker (e.g. Carpenter, Plumber, Painter, Bus Driver, Pub/Bar worker)' },
      { id: 'opt_6', value: '6', text: 'Semi or unskilled manual work (e.g. Manual worker, Caretaker, Park Keeper, Shop Assistant)' },
      { id: 'opt_7', value: '7', text: 'Casual worker - not in permanent employment' },
      { id: 'opt_8', value: '8', text: 'Housewife/ Home-maker' },
      { id: 'opt_9', value: '9', text: 'Retired' },
      { id: 'opt_10', value: '10', text: 'Farmer 1-49 acres' },
      { id: 'opt_11', value: '11', text: 'Farmer 50+ acres' },
      { id: 'opt_12', value: '12', text: 'Unemployed or not working due to long-term sickness' }
    ] as QuestionOption[];

    const questionFormToAdd = QuestionFormInstanceByType(QuestionType.SingleChoice);
    questionFormToAdd.init(questionToAdd);

    return questionFormToAdd;
  }

  mapSocialGroupHiddenQuestion(nextId: number, occupationQuestionId: string): QuestionForm {
    const questionToAdd = new Question();
    questionToAdd.id = `${QuestionPrefix.HiddenQuestion}${nextId}`;
    questionToAdd.type = QuestionType.HiddenChoice;
    questionToAdd.title = 'Social Group (based on Occupation)';
    questionToAdd.options = [
      {
        id: 'opt_1',
        value: '1',
        text: 'AB',
        selectedWhen: [{ questionId: occupationQuestionId, operator: 'EQUAL_ANY', valueToMatch: ['opt_1', 'opt_2'] }]
      },
      {
        id: 'opt_2',
        value: '2',
        text: 'C1',
        selectedWhen: [{ questionId: occupationQuestionId, operator: 'EQUAL_ANY', valueToMatch: ['opt_3', 'opt_4'] }]
      },
      {
        id: 'opt_3',
        value: '3',
        text: 'C2',
        selectedWhen: [{ questionId: occupationQuestionId, operator: 'EQUAL', valueToMatch: ['opt_5'] }]
      },
      {
        id: 'opt_4',
        value: '4',
        text: 'DE',
        selectedWhen: [{ questionId: occupationQuestionId, operator: 'EQUAL_ANY', valueToMatch: ['opt_6', 'opt_7', 'opt_8', 'opt_9', 'opt_12'] }]
      },
      {
        id: 'opt_5',
        value: '5',
        text: 'F',
        selectedWhen: [{ questionId: occupationQuestionId, operator: 'EQUAL_ANY', valueToMatch: ['opt_10', 'opt_11'] }]
      }
    ] as QuestionOption[];

    const questionFormToAdd = QuestionFormInstanceByType(QuestionType.HiddenChoice);
    questionFormToAdd.init(questionToAdd);

    return questionFormToAdd;
  }

  mapSocialClassHiddenQuestion(nextId: number, occupationQuestionId: string): QuestionForm {
    const questionToAdd = new Question();
    questionToAdd.id = `${QuestionPrefix.HiddenQuestion}${nextId}`;
    questionToAdd.type = QuestionType.HiddenChoice;
    questionToAdd.title = 'Social Class (based on Occupation)';
    questionToAdd.options = [
      {
        id: 'opt_1',
        value: '1',
        text: 'ABC1F+',
        selectedWhen: [{ questionId: occupationQuestionId, operator: 'EQUAL_ANY', valueToMatch: ['opt_1', 'opt_2', 'opt_3', 'opt_4', 'opt_11'] }]
      },
      {
        id: 'opt_2',
        value: '2',
        text: 'C2DEF-',
        selectedWhen: [
          { questionId: occupationQuestionId, operator: 'EQUAL_ANY', valueToMatch: ['opt_5', 'opt_6', 'opt_7', 'opt_8', 'opt_9', 'opt_10', 'opt_12'] }
        ]
      }
    ] as QuestionOption[];

    const questionFormToAdd = QuestionFormInstanceByType(QuestionType.HiddenChoice);
    questionFormToAdd.init(questionToAdd);

    return questionFormToAdd;
  }

  mapChildrenQuestion(nextId: number, pageId: string): QuestionForm {
    const questionToAdd = new Question();
    questionToAdd.id = `${QuestionPrefix.Question}${nextId}`;
    questionToAdd.pageId = pageId;
    questionToAdd.type = QuestionType.SingleChoice;
    questionToAdd.title = 'Do you have any children under 18 living with you?';
    questionToAdd.options = [
      { id: 'opt_1', value: '1', text: 'Yes' },
      { id: 'opt_2', value: '2', text: 'No' }
    ] as QuestionOption[];

    const questionFormToAdd = QuestionFormInstanceByType(QuestionType.SingleChoice);
    questionFormToAdd.init(questionToAdd);

    return questionFormToAdd;
  }

  mapChildrenAgeQuestion(nextId: number, pageId: string, prevId: number): QuestionForm {
    const questionToAdd = new Question();
    questionToAdd.id = `${QuestionPrefix.Question}${nextId}`;
    questionToAdd.pageId = pageId;
    questionToAdd.type = QuestionType.Carousel;
    questionToAdd.title = 'Do you have any dependent children living with you who are aged ...?';
    questionToAdd.cards = [
      { id: 'card_1', text: '0-4 years' },
      { id: 'card_2', text: '5-12 years' },
      { id: 'card_3', text: '13-17 years' },
      { id: 'card_4', text: '18-24 years' },
      { id: 'card_5', text: '25 or older' }
    ] as QuestionCard[];

    questionToAdd.options = [
      { id: 'opt_1', value: '1', text: 'Yes' },
      { id: 'opt_2', value: '2', text: 'No' }
    ] as QuestionOption[];

    questionToAdd.visibleWhen = [{ questionId: `${QuestionPrefix.Question}${prevId}`, operator: 'EQUAL', valueToMatch: ['opt_1'] } as Condition];

    const questionFormToAdd = QuestionFormInstanceByType(QuestionType.Carousel);
    questionFormToAdd.init(questionToAdd);

    return questionFormToAdd;
  }
}
